import React, { ReactElement, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import DeallocateSalesOrdersMutation from '../../graphql/mutations/sales/DeallocateSalesOrdersMutation.graphql';
import UnReserveSalesOrdersMutation from '../../graphql/mutations/sales/UnReserveSalesOrdersMutation.graphql';
import InvoiceApprovalToggleMutation from '../../graphql/mutations/sales/InvoiceApprovalToggleMutation.graphql';
import CashReceivedToggleMutation from '../../graphql/mutations/sales/CashReceivedToggleMutation.graphql';
import ConsolidatedPickGenerateFromSalesOrdersMutation from '../../graphql/mutations/sales/ConsolidatedPickGenerateFromSalesOrdersMutation.graphql';
import SalesOrderActivationModal from '../../components/modals/SalesOrderActivationModal';
import RescheduleOrdersModal from '../../components/modals/RescheduleOrdersModal';
import { useDispatchAction } from '../../components/ActionButtons/dispatch/DispatchAction';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { useBasicWorkspaceAction, useWorkspaceAction } from '../../components/Workspace/WorkspaceActionButtons';
import { useActionCriteria } from '../../utils/useActionProps';
import { IDeprecatedWorkspaceAction, IWorkspaceAction } from '../../@types/workspaceTypes';
import { useGlobalAmendAction } from './SalesOrderGlobalAmendAction';
import { WorkspaceActionHeader } from '../../components/ActionButtons/ActionHeader';
import ModalContext from '../../components/Context/ModalContext';
import { Allocation, ConsolidatedPick, ProductLabel } from '../../graphql/entities';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { SalesOrderExportToEdi } from '../../graphql/mutations/sales/SalesOrderExportToEdi.graphql';
import { useHistory } from 'react-router';
import AllocateSalesOrdersMutation from '../../graphql/mutations/sales/AllocateSalesOrdersMutation.graphql';
import { DispatchSource } from '../../components/ActionButtons/dispatch/DispatchModal';
import SalesOrderNotifyMaxoptraMutation from '../../graphql/mutations/sales/SalesOrdersNotifyMaxoptraMutation.graphql';
import { useFullUserDetails } from '../../utils/hooks';
import MaxoptraNotificationResultsModal from '../../views/sales/MaxoptraNotificationResultsModal';
import { IJobMeta } from '../../utils/BackgroundJobs';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import Notification from '../../components/Notification';
import { AlertType, MaxoptraOrderPriority, ReportType, Maybe } from '../../graphql/schema/p4types';
import getToastId from '../../utils/getToastId';
import LinkNotification from '../../components/Notification/LinkNotification';
import { useP4DocumentController } from '../../components/document/DocumentProvider';
import generateClientMutationId from '../../graphql/schema/generateClientMutationId';
import { DocumentNode } from 'graphql';

export enum SalesWorkspaceActions {
  Activate = 'SALES_ORDER_ACTIVATE',
  SalesOrderGlobalAmend = 'Sales Order Global Amend',
  NotifyMaxoptra = 'Notify Maxoptra',
  RescheduleOrders = 'Reschedule Orders',
  ConsolidatedPick = 'Consolidated Pick',
}

export const workspaceForPermissions = [
  {
    description: 'Activate',
    actionReference: 'ACTIVATE',
  },
  {
    description: 'Allocate',
    actionReference: 'SALES_PRODUCT_ALLOCATION',
  },
  {
    description: 'Deallocate',
    actionReference: 'SALES_PRODUCT_DEALLOCATION',
  },
  {
    description: 'Unreserve',
    actionReference: 'SALES_ORDER_UNRESERVE',
  },
  {
    description: 'Dispatch',
    actionReference: 'MANUAL_DISPATCH',
  },
  {
    description: 'Approve/Unapprove for Invoicing',
    actionReference: 'SALES_LEDGER_INVOICES_UPDATE',
  },
  {
    description: 'Toggle Cash Received',
    actionReference: 'SALES_ORDERS_CREATE',
  },
  {
    description: 'Invoice',
    actionReference: 'SALES_INVOICE_GENERATION_RESULTS',
  },
  {
    actionReference: 'EXPORT_TO_EDI',
  },
  {
    actionReference: 'PRINT_LABELS',
  },
  { actionReference: SalesWorkspaceActions.SalesOrderGlobalAmend },
  { actionReference: SalesWorkspaceActions.NotifyMaxoptra },
  { actionReference: SalesWorkspaceActions.RescheduleOrders },
  { actionReference: SalesWorkspaceActions.ConsolidatedPick },
];

const usePrintSalesOrderLabelsAction: () => IWorkspaceAction[] = () => {
  const { selectionState } = useContext(WorkspaceContext);
  const entities = selectionState.actionEntities;
  const { printDocumentSingle } = useP4DocumentController();
  const { dispatchSimpleToast } = useP4ToastController();

  const printLabels = () => {
    if (entities.length > 20) {
      dispatchSimpleToast(
        'Too Many Sales Orders Selected',
        'Cannot generate labels for more than 20 sales orders at a time',
        AlertType.Warning
      );
      return;
    }

    if (selectionState?.selectedAll) {
      dispatchSimpleToast(
        'Invalid Selection',
        'Select all not available for printing sales order labels.  Please select the sales orders you wish to print labels for',
        AlertType.Warning
      );
      return;
    }

    entities.forEach((entity) => {
      printDocumentSingle(ReportType.SalesOrderLabel, entity.id);
    });
  };

  const printLabelsAction: IWorkspaceAction = {
    key: 'printSalesOrderLabels',
    text: 'Print Sales Order Labels',
    onClick: () => printLabels(),
    actionReference: 'PRINT_SALES_ORDER_LABELS',
    header: WorkspaceActionHeader.REPORTS,
  };

  return [printLabelsAction];
};

const usePrintLabelsAction: () => [IWorkspaceAction, ReactElement] = () => {
  const [printLabelModalOpen, setPrintLabelModalOpen] = useState(false);
  const { selectionState } = useContext(WorkspaceContext);

  const printLabelsAction: IWorkspaceAction = {
    key: 'printLabels',
    text: 'Print Labels',
    onClick: () => setPrintLabelModalOpen(true),
    actionReference: 'PRINT_LABELS',
    header: WorkspaceActionHeader.REPORTS,
  };

  const printLabelsActionResult: [IWorkspaceAction, ReactElement] = [
    printLabelsAction,
    <ModalContext.Provider
      value={{
        onClose: () => {
          setPrintLabelModalOpen(false);
        },
      }}
    >
      <DocumentModal2
        modalOpen={printLabelModalOpen}
        multiValue={true}
        entity={ProductLabel}
        useCustomerDefaults={false}
        title="Print Labels"
        modalWrapper={true}
        blockNavigation={true}
        allowEmail={true}
        outputSelection={[
          {
            value: 'LOCAL_PRINTER',
            label: 'Local Printer',
          },
          {
            value: 'SYSTEM_PRINTER',
            label: 'System Printer',
          },
        ]}
        count={selectionState.count}
        type={'salesOrders'}
      />
    </ModalContext.Provider>,
  ];

  return printLabelsActionResult;
};

const useInvoiceGenerationAction = (): [IWorkspaceAction] => {
  const workspaceContext = useContext(WorkspaceContext);
  const history = useHistory();
  return [
    {
      key: 'invoice',
      text: 'Invoice',
      actionReference: 'SALES_INVOICE_GENERATION_RESULTS',
      header: WorkspaceActionHeader.PROCESS_ORDERS,
      onClick: () => {
        history.push({
          pathname: '/Sales/InvoiceGenerationCriteria/Results',
          state: {
            selectionState: workspaceContext.selectionState,
            previousLocation: history.location?.pathname || 'back',
          },
        });
      },
    },
  ];
};

const useSalesOrderActivationAction = (): [IWorkspaceAction<SalesWorkspaceActions>, ReactElement] => {
  const [isOpen, setIsOpen] = useState(false);

  return [
    {
      key: 'activate',
      text: 'Activate',
      onClick: () => setIsOpen(true),
      actionReference: SalesWorkspaceActions.Activate,
      header: WorkspaceActionHeader.PROCESS_ORDERS,
    },
    <SalesOrderActivationModal isOpen={isOpen} onClose={() => setIsOpen(false)} />,
  ];
};

const useAllocateAction = () => {
  const { dispatchToast, dismissToast } = useP4ToastController();

  const onCompleteJob = useCallback(
    (job: IJobMeta) => {
      const allocationToastId = `${job.jobId}-result`;

      dispatchToast(
        <LinkNotification
          type={AlertType.Success}
          title={'Stock allocation completed'}
          link={`${Allocation.workspaceUrl}${job.jobId}`}
          linkTitle={'View Allocations'}
          onClose={() => {
            dismissToast(allocationToastId);
          }}
        />,
        {
          title: 'Stock allocation completed',
          type: AlertType.Action,
          toastId: allocationToastId,
          intent: 'success',
        }
      );
    },
    [dispatchToast, dismissToast]
  );

  return useBasicWorkspaceAction({
    mutation: AllocateSalesOrdersMutation,
    errorMessage: 'Could not allocate Sales Orders',
    text: 'Allocate',
    actionReference: 'SALES_PRODUCT_ALLOCATION',
    header: WorkspaceActionHeader.PROCESS_ORDERS,
    onComplete: onCompleteJob,
  });
};

export const useNotifyMaxoptra = (
  mutation: DocumentNode,
  actionReference: string,
  actionKey: string,
  maxoptraOrderPriority?: Maybe<MaxoptraOrderPriority>,
  effectiveOrderDate?: Maybe<string>
): [IWorkspaceAction<string>, ReactElement] => {
  const [isOpen, setIsOpen] = useState(false);
  const [batchId, setBatchId] = useState(0);
  const { dispatchToast } = useP4ToastController();

  const actionCriteria = useActionCriteria();

  const variables = useMemo(() => {
    return {
      input: {
        clientMutationId: generateClientMutationId(),
        actionCriteria: actionCriteria,
        maxoptraOrderPriority: maxoptraOrderPriority,
        effectiveOrderDate: effectiveOrderDate,
      },
    };
  }, [actionCriteria, maxoptraOrderPriority, effectiveOrderDate]);

  const onClick = useWorkspaceAction({
    mutation: mutation,
    errorMessage: 'Could not notify Maxoptra',
    variables,
    onComplete: (job: IJobMeta) => {
      if ((job?.recordsFailed ?? 0) > 0) {
        setBatchId(job?.properties?.maxoptraNotificationBatchId ?? 0);
        setIsOpen(true);
      }

      const recordsProcessed = job?.recordsProcessed ?? 0;
      if (recordsProcessed > 0) {
        const toastId = getToastId();
        const message = `${recordsProcessed} sales order(s) notified`;
        dispatchToast(
          <Notification
            title="Notify Maxoptra"
            message={message}
            type={AlertType.Success}
            key={'notifyMaxoptraResult'}
          />,
          {
            intent: 'info',
            toastId: toastId,
            title: 'Notify Maxoptra',
            type: AlertType.Success,
            message: message,
          }
        );
      }

      if ((job?.recordsFailed ?? 0) > 0) {
        const toastId = getToastId();
        const message = `One or more sales orders failed to notify or were notified with issues`;
        dispatchToast(
          <Notification
            title="Notify Maxoptra"
            message={message}
            type={AlertType.Warning}
            key={'notifyMaxoptraResult'}
          />,
          {
            intent: 'info',
            toastId: toastId,
            title: 'Notify Maxoptra',
            type: AlertType.Warning,
            message: message,
          }
        );
      }
    },
  });

  const notifyMaxoptraAction: IWorkspaceAction<string> = {
    key: actionKey,
    text: 'Notify Maxoptra',
    onClick: onClick,
    actionReference: actionReference,
    header: WorkspaceActionHeader.MAXOPTRA,
  };

  return [
    notifyMaxoptraAction,
    <MaxoptraNotificationResultsModal isOpen={isOpen} batchId={batchId} onClose={() => setIsOpen(false)} />,
  ];
};

/**
 * Maxoptra notification action for sales order workspace.
 * Supports priority and effective order date parameters which can be captured via RescheduleOrdersModal.
 *
 * @param maxoptraOrderPriority Optional priority to set on the sales orders
 * @param effectiveOrderDate Optional effective order date - if provided and auto-assign is enabled, the target date will be recalculated
 */
export const useNotifyMaxoptraAction = (
  maxoptraOrderPriority?: Maybe<MaxoptraOrderPriority>,
  effectiveOrderDate?: Maybe<string>
): [IWorkspaceAction<string>, ReactElement] => {
  return useNotifyMaxoptra(
    SalesOrderNotifyMaxoptraMutation,
    SalesWorkspaceActions.NotifyMaxoptra,
    'notifyMaxOptraForSalesOrders',
    maxoptraOrderPriority,
    effectiveOrderDate
  );
};

const useRescheduleOrdersAction = (): [IWorkspaceAction<SalesWorkspaceActions>, ReactElement] => {
  const [isOpen, setIsOpen] = useState(false);

  return [
    {
      key: 'rescheduleOrders',
      text: 'Reschedule Orders',
      onClick: () => setIsOpen(true),
      actionReference: SalesWorkspaceActions.RescheduleOrders,
      header: WorkspaceActionHeader.MAXOPTRA,
    },
    <RescheduleOrdersModal isOpen={isOpen} onClose={() => setIsOpen(false)} />,
  ];
};

export const useSalesWorkspaceActions: () => [
  Array<IWorkspaceAction | IDeprecatedWorkspaceAction>,
  ReactNode | ReactElement
] = () => {
  const fullUserDetails = useFullUserDetails();
  const { dispatchToast, dismissToast } = useP4ToastController();

  const allocateAction = useAllocateAction();
  const deallocateAction = useBasicWorkspaceAction({
    mutation: DeallocateSalesOrdersMutation,
    errorMessage: 'Could not deallocate orders',
    text: 'Deallocate',
    actionReference: 'SALES_PRODUCT_DEALLOCATION',
    header: WorkspaceActionHeader.PROCESS_ORDERS,
  });
  const exportToEdi = useBasicWorkspaceAction({
    mutation: SalesOrderExportToEdi,
    errorMessage: 'Could not export to EDI',
    text: 'Export to EDI',
    actionReference: 'EXPORT_TO_EDI',
    header: WorkspaceActionHeader.REPORTS,
  });
  const unReserveAction = useBasicWorkspaceAction({
    mutation: UnReserveSalesOrdersMutation,
    errorMessage: 'Could not unreserve orders',
    text: 'Unreserve',
    actionReference: 'SALES_ORDER_UNRESERVE',
    header: WorkspaceActionHeader.PROCESS_ORDERS,
  });
  const approveUnapproveInvoiceAction = useBasicWorkspaceAction({
    mutation: InvoiceApprovalToggleMutation,
    errorMessage: 'Could not toggle invoice approval',
    text: 'Approve/Unapprove for Invoicing',
    actionReference: 'SALES_LEDGER_INVOICES_UPDATE',
    header: WorkspaceActionHeader.STATUS_UPDATE,
  });
  const toggleCashReceivedAction = useBasicWorkspaceAction({
    mutation: CashReceivedToggleMutation,
    errorMessage: 'Could not toggle cash received',
    text: 'Toggle Cash Received',
    actionReference: 'SALES_ORDERS_CREATE',
    header: WorkspaceActionHeader.STATUS_UPDATE,
  });

  const [dispatchActionButton, dispatchPrintModals] = useDispatchAction({
    source: DispatchSource.SalesOrderWorkspace,
  });
  const [globalAmendAction, globalAmendModals] = useGlobalAmendAction();
  const [printLabelsAction, printLabelsModals] = usePrintLabelsAction();
  const [printSalesOrderLabelAction] = usePrintSalesOrderLabelsAction();

  const [invoiceGenerationAction] = useInvoiceGenerationAction();

  const consolidatedPickAction = useBasicWorkspaceAction({
    mutation: ConsolidatedPickGenerateFromSalesOrdersMutation,
    errorMessage: 'Could not generate consolidated pick',
    text: 'Consolidated Pick',
    actionReference: SalesWorkspaceActions.ConsolidatedPick,
    header: WorkspaceActionHeader.PROCESS_ORDERS,
    requiresActionCriteria: true,
    condition: () => fullUserDetails?.flags?.consolidatedPickEnabled ?? false,
    onComplete: (job) => {
      const consolidatedPickId = job.properties?.consolidatedPickId;

      if (consolidatedPickId) {
        const toastId = `consolidated-pick-${consolidatedPickId}-result`;
        dispatchToast(
          <LinkNotification
            type={AlertType.Success}
            title="Consolidated Pick Created Successfully"
            link={`${ConsolidatedPick.formUrl}${consolidatedPickId}`}
            linkTitle="View Consolidated Pick"
            onClose={() => {
              dismissToast(toastId);
            }}
          />,
          {
            title: 'Consolidated Pick Created Successfully',
            type: AlertType.Action,
            toastId: toastId,
            intent: 'success',
          }
        );
      }
    },
  });

  const [salesOrderActivationAction, salesOrderActivationModals] = useSalesOrderActivationAction();

  const [notifyMaxoptraAction, notifyMaxoptraModals] = useNotifyMaxoptraAction();

  const [rescheduleOrdersAction, rescheduleOrdersModals] = useRescheduleOrdersAction();

  const actions: Array<IWorkspaceAction | IDeprecatedWorkspaceAction> = useMemo(() => {
    const actionArray: Array<IWorkspaceAction | IDeprecatedWorkspaceAction> = [
      salesOrderActivationAction,
      allocateAction,
      deallocateAction,
      exportToEdi,
      unReserveAction,
      dispatchActionButton,
      approveUnapproveInvoiceAction,
      toggleCashReceivedAction,
      invoiceGenerationAction,
      printLabelsAction,
      printSalesOrderLabelAction,
      globalAmendAction,
      consolidatedPickAction,
    ];

    if (fullUserDetails?.flags?.maxoptraEnabled) {
      actionArray.push(notifyMaxoptraAction);
      actionArray.push(rescheduleOrdersAction);
    }

    return actionArray;
  }, [
    salesOrderActivationAction,
    allocateAction,
    approveUnapproveInvoiceAction,
    deallocateAction,
    dispatchActionButton,
    exportToEdi,
    fullUserDetails?.flags?.maxoptraEnabled,
    globalAmendAction,
    invoiceGenerationAction,
    notifyMaxoptraAction,
    rescheduleOrdersAction,
    printLabelsAction,
    toggleCashReceivedAction,
    unReserveAction,
    printSalesOrderLabelAction,
    consolidatedPickAction,
  ]);

  return [
    actions,
    <>
      {dispatchPrintModals}
      {globalAmendModals}
      {printLabelsModals}
      {salesOrderActivationModals}
      {notifyMaxoptraModals}
      {rescheduleOrdersModals}
    </>,
  ];
};
